import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';

// MY DECISIONS (owner 2026-10-30): every pick you approved / rejected / left unanswered with its result and WHY it
// went to profit or loss, the patterns the AI remembers (re-tested every evening on the older and newer half of the
// days), the chance of profit for each kind of pick, how much it gave, and the precautions. Admin only.
const C = { win: 'var(--profit, #16c784)', loss: 'var(--loss, #ef4757)', warn: 'var(--warn, #f5a623)', dim: 'var(--text-3, #6b7488)', txt2: 'var(--text-2, #9aa4b7)' };
const pc = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`);
const rs = (v) => (v == null ? '—' : `${v >= 0 ? '+' : '−'}₹${Math.abs(Math.round(v)).toLocaleString('en-IN')}`);
const col = (v) => (v == null ? C.dim : v > 0 ? C.win : v < 0 ? C.loss : C.txt2);
const VERD = { GOOD: ['✔ GOOD', C.win], BAD: ['✖ BAD', C.loss], MIXED: ['≈ MIXED', C.warn], 'TOO FEW': ['… too few', C.dim], FADED: ['faded', C.dim] };

function Verdict({ v }) {
  const [t, c] = VERD[v] || [v, C.dim];
  return <span style={{ color: c, fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{t}</span>;
}

function Tile({ p }) {
  return (
    <div className="card" style={{ margin: 0, padding: '0.7rem 0.9rem', minWidth: 0 }}>
      <div style={{ color: C.txt2, fontSize: '0.78rem', marginBottom: 4 }}>{p.label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: col(p.avg_pct) }}>{p.win_pct}% won</div>
      <div style={{ fontSize: '0.8rem', color: C.txt2 }}>
        avg <b style={{ color: col(p.avg_pct) }}>{pc(p.avg_pct)}</b> · {p.n} picks{p.rs != null ? <> · <b style={{ color: col(p.rs) }}>{rs(p.rs)}</b></> : ''}
      </div>
      <div style={{ fontSize: '0.72rem', color: C.dim, marginTop: 2 }}>wins avg {pc(p.avg_win_pct)} · losses avg {pc(p.avg_loss_pct)}</div>
    </div>
  );
}

function Section({ title, sub, children, open = true }) {
  const [o, setO] = useState(open);
  return (
    <div className="card" style={{ marginBottom: '0.9rem' }}>
      <div onClick={() => setO(!o)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>{title}</div>
          {sub && <div style={{ color: C.dim, fontSize: '0.78rem', marginTop: 2 }}>{sub}</div>}
        </div>
        <span style={{ color: C.dim }}>{o ? '▾' : '▸'}</span>
      </div>
      {o && <div style={{ marginTop: '0.7rem' }}>{children}</div>}
    </div>
  );
}

// INDEX-ONLY MODE (owner 2026-11-03): stock options trade only in the background (virtual); this shows when that book is
// mature enough to trade for real, and the index vs stock ML models side by side.
const BG_VERD = { READY: C.win, MIXED: C.warn, 'NOT READY': C.loss, COLLECTING: C.dim };

function BgBlock({ t, g }) {
  return (
    <div className="card" style={{ margin: 0, padding: '0.6rem 0.8rem' }}>
      <div style={{ color: C.txt2, fontSize: '0.78rem' }}>{t}</div>
      <div style={{ fontSize: '1.2rem', fontWeight: 800, color: col(g.all && g.all.rs) }}>{g.all && g.all.n ? rs(g.all.rs) : '—'}</div>
      <div style={{ fontSize: '0.78rem', color: C.txt2 }}>
        {g.all && g.all.n ? `${g.all.n} trades · ${g.all.win_pct}% won · ${rs(g.all.per_trade)} per trade` : 'no results yet'}
      </div>
      {g.all && g.all.n ? (
        <div style={{ fontSize: '0.72rem', color: C.dim, marginTop: 2 }}>
          older half {g.older.n ? rs(g.older.per_trade) : '—'}/trade · newer half {g.newer.n ? rs(g.newer.per_trade) : '—'}/trade · {g.days} days
        </div>) : null}
    </div>
  );
}

function MlCard({ t, m, extra }) {
  if (!m) return null;
  const auc = (v) => (v == null ? '—' : Number(v).toFixed(3));
  return (
    <div className="card" style={{ margin: 0, padding: '0.6rem 0.8rem' }}>
      <div style={{ color: C.txt2, fontSize: '0.78rem' }}>{t}</div>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, color: m.test_auc >= 0.6 ? C.win : m.test_auc > 0.55 ? C.warn : C.dim }}>
        {m.loaded ? `AUC ${auc(m.test_auc)}` : 'no own model yet'}
      </div>
      <div style={{ fontSize: '0.75rem', color: C.txt2 }}>
        {m.loaded ? `${m.model_name || 'model'} · ${m.trained_on} examples · ${m.trained_at || ''}` : 'trains tonight after 15:40'}
        {m.answers_with && m.answers_with !== m.kind ? ` · now answered by the combined model` : ''}
      </div>
      <div style={{ fontSize: '0.72rem', color: C.dim, marginTop: 2 }}>
        on real results: AUC {auc(m.live_auc)} ({m.live_n || 0} trades){extra}
      </div>
    </div>
  );
}

function StockBackground({ b }) {
  const v = b.verdict || 'COLLECTING';
  return (
    <Section title={`📊 Stock options in the background ${b.real_on ? '(real trading is ON)' : '(INDEX-ONLY mode - virtual, no money)'}`}
             sub="Every stock pick the bot would have traded is 'bought' virtually: 1 lot at the real option price + spread + Rs.110 charges, normal stock-option exits.">
      <div style={{ padding: '0.6rem 0.8rem', borderRadius: 8, border: `1px solid ${BG_VERD[v] || C.dim}`, marginBottom: 8 }}>
        <div style={{ fontWeight: 800, color: BG_VERD[v] || C.dim }}>{v === 'READY' ? '✔ ' : ''}{v}</div>
        <div style={{ fontSize: '0.85rem', color: C.txt2 }}>{b.verdict_text}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8, marginBottom: 8 }}>
        <BgBlock t="Would trade - passes every check (decides READY)" g={b.bot_pass} />
        <BgBlock t="All bot picks (incl. ones a later check stopped)" g={b.bot} />
        <BgBlock t="Signals the bot skipped (wide net)" g={b.skipped} />
      </div>
      {b.bot_pass && b.bot_pass.last5 && b.bot_pass.last5.n ? (
        <div style={{ fontSize: '0.8rem', color: C.txt2, marginBottom: 6 }}>
          Last 5 days (would trade): <b style={{ color: col(b.bot_pass.last5.rs) }}>{rs(b.bot_pass.last5.rs)}</b> on {b.bot_pass.last5.n} trades ·
          by ML: {Object.entries(b.bot.by_ml || {}).map(([k, a]) => `${k}: ${a.n ? `${a.n} ${rs(a.rs)}` : '—'}`).join(' · ')}
        </div>) : null}
      {(b.history || []).length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', margin: '0.6rem 0 0.3rem' }}>Daily maturity check (every evening after 15:40)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead><tr style={{ color: C.dim, textAlign: 'left' }}>
                <th>Day</th><th>Verdict</th><th>Would trade (total)</th><th>Per trade</th><th>Today: bot picks</th><th>Today: skipped</th><th>Stock ML AUC</th>
              </tr></thead>
              <tbody>{b.history.map((h) => {
                const tb = (h.today || {}).bot || {}; const ts = (h.today || {}).skipped || {};
                return (
                  <tr key={h.day} style={{ borderTop: '1px solid var(--border, #232c3b)' }}>
                    <td style={{ padding: '3px 4px', whiteSpace: 'nowrap' }}>{h.day}</td>
                    <td style={{ color: BG_VERD[h.verdict] || C.dim, fontWeight: 700 }}>{h.verdict}</td>
                    <td>{h.pass_n} · <span style={{ color: col(h.pass_rs) }}>{rs(h.pass_rs)}</span></td>
                    <td style={{ color: col(h.pass_per_trade) }}>{h.pass_n ? rs(h.pass_per_trade) : '—'}</td>
                    <td>{tb.n ? <>{tb.n} · <span style={{ color: col(tb.rs) }}>{rs(tb.rs)}</span></> : '—'}</td>
                    <td>{ts.n ? <>{ts.n} · <span style={{ color: col(ts.rs) }}>{rs(ts.rs)}</span></> : '—'}</td>
                    <td>{h.stock_live_auc ?? '—'}</td>
                  </tr>);
              })}</tbody>
            </table>
          </div>
        </>
      )}
      {(b.newinfo || []).length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', margin: '0.6rem 0 0.2rem' }}>New information test - what the price indicators cannot see</div>
          <div style={{ fontSize: '0.72rem', color: C.dim, marginBottom: 4 }}>Recorded with every background stock trade (owner 3 Nov). A signal is worth using only when its 'with' group earns per trade in BOTH the older and newer half, and its 'against' group does worse.</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead><tr style={{ color: C.dim, textAlign: 'left' }}><th>Signal</th><th>Group</th><th>Trades</th><th>Won</th><th>Per trade</th><th>Older half</th><th>Newer half</th></tr></thead>
              <tbody>{b.newinfo.flatMap((f) => (f.groups || []).map((g, i) => (
                <tr key={f.signal + g.group} style={{ borderTop: i === 0 ? '1px solid var(--border, #232c3b)' : 'none' }}>
                  <td style={{ padding: '3px 4px', color: C.txt2 }}>{i === 0 ? f.signal : ''}</td><td>{g.group}</td><td>{g.n}</td><td>{g.win_pct}%</td>
                  <td style={{ color: col(g.per_trade) }}>{rs(g.per_trade)}</td>
                  <td style={{ color: col(g.older) }}>{g.older == null ? '—' : rs(g.older)} <span style={{ color: C.dim }}>({g.n_old})</span></td>
                  <td style={{ color: col(g.newer) }}>{g.newer == null ? '—' : rs(g.newer)} <span style={{ color: C.dim }}>({g.n_new})</span></td>
                </tr>)))}</tbody>
            </table>
          </div>
        </>
      )}
      <div style={{ fontWeight: 700, fontSize: '0.88rem', margin: '0.6rem 0 0.4rem' }}>AI models - separate for index and stock options</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
        <MlCard t="Index options model" m={b.ml && b.ml.index} extra="" />
        <MlCard t="Stock options model" m={b.ml && b.ml.stock}
                extra={b.ml && b.ml.stock && b.ml.stock.bg_n ? ` · background book AUC ${b.ml.stock.bg_auc == null ? '—' : b.ml.stock.bg_auc} (${b.ml.stock.bg_n})` : ''} />
      </div>
      <div style={{ fontSize: '0.72rem', color: C.dim, marginTop: 4 }}>AUC 0.50 = coin flip. AUC = held-out test when trained; 'on real results' = how its win-chance matched what really happened.</div>
      <div style={{ marginTop: 8 }}>
        {(b.recent || []).filter((r) => r.kind === 'bot').slice(0, 20).map((r, i) => (
          <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: '0.78rem', borderTop: '1px solid var(--border, #232c3b)', padding: '3px 0' }}>
            <span style={{ color: C.dim }}>{r.day.slice(5)} {r.time.slice(0, 5)}</span><b>{r.ts_}</b>
            <span style={{ color: r.pass ? C.win : C.warn }}>{r.pass ? 'would trade' : `stopped: ${(r.checks || []).join('; ').slice(0, 70)}`}</span>
            <span style={{ color: C.dim }}>score {r.score}{r.ml != null ? ` · ML ${Math.round(r.ml * 100)}%` : ''}</span>
            <span style={{ marginLeft: 'auto', color: col(r.rs), fontWeight: 700 }}>{pc(r.pct)} {rs(r.rs)} <span style={{ color: C.dim, fontWeight: 400 }}>({r.exit}, {r.mins} min)</span></span>
          </div>))}
      </div>
    </Section>
  );
}

function DecisionRow({ r }) {
  const [o, setO] = useState(false);
  const res = r.result;
  return (
    <div style={{ borderTop: '1px solid var(--border, #232c3b)', padding: '0.55rem 0' }}>
      <div onClick={() => setO(!o)} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 10px', alignItems: 'baseline', cursor: 'pointer' }}>
        <span style={{ color: C.dim, fontSize: '0.75rem', minWidth: 92 }}>{String(r.at || '').slice(5, 16).replace('T', ' ')}</span>
        <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{r.trade_symbol}</span>
        <span style={{ fontSize: '0.75rem', color: C.txt2 }}>{r.choice}</span>
        <span style={{ marginLeft: 'auto', fontWeight: 800, color: res ? col(r.pct) : C.dim, fontSize: '0.9rem' }}>
          {res ? (res === 'profit' ? '▲ ' : '▼ ') : ''}{r.pct != null ? pc(r.pct) : 'waiting for result'}
          {r.rs != null && <span style={{ marginLeft: 6 }}>{rs(r.rs)}</span>}
          {r.shadow && r.pct != null && <span style={{ color: C.dim, fontWeight: 400, fontSize: '0.7rem', marginLeft: 4 }}>(would have)</span>}
        </span>
      </div>
      {res && r.why && r.why.length > 0 && (
        <div style={{ marginTop: 3 }}>
          {r.why.slice(0, o ? 10 : 1).map((w, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: w.startsWith('hurt') ? '#ffb3b3' : w.startsWith('helped') ? '#a8f0cf' : C.txt2 }}>• {w}</div>
          ))}
        </div>
      )}
      {o && (
        <div style={{ marginTop: 4, fontSize: '0.74rem', color: C.dim }}>
          {r.skip_reason && <div>Why the bot did not auto-trade it: {r.skip_reason.replace('AI pick NOT auto-traded - skipped by: ', '')}</div>}
          {r.users && r.users.length > 0 && <div>Traded by: {r.users.join(', ')} · all users together {rs(r.rs_all)}</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
            {(r.factors || []).map((f) => <span key={f} style={{ border: '1px solid var(--border-2, #2e3a4d)', borderRadius: 10, padding: '1px 7px' }}>{f}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DecisionLab() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [view, setView] = useState('real');
  const [filt, setFilt] = useState('all');
  const fails = useRef(0);

  useEffect(() => {
    let alive = true; let got = false; let tm = null;
    const f = async () => {
      try {
        const r = await api.decisionLab();
        if (!alive) return;
        if (r && r.status === 'success') { setD(r); setErr(null); fails.current = 0; got = true; }
        else if (!got || ++fails.current >= 3) setErr((r && r.message) || 'not responding');
      } catch (e) { if (alive && (!got || ++fails.current >= 3)) setErr('server not responding - trying again'); }   // keep the last good data
      // until the first answer: retry every 10 s (not a silent 'Loading' for minutes); then every 2 min
      if (alive) tm = setTimeout(f, got ? 120000 : 10000);
    };
    f();
    return () => { alive = false; clearTimeout(tm); };
  }, []);

  const lab = d ? (view === 'real' ? d.real : d) : null;
  const rows = useMemo(() => {
    const all = (d && d.rows) || [];
    return all.filter((r) => filt === 'all' || (filt === 'profit' && r.result === 'profit') || (filt === 'loss' && r.result === 'loss')
      || (filt === 'approved' && ['approve', 'approve_forced', 'manual'].includes(r.decision)) || (filt === 'rejected' && r.decision === 'reject')
      || (filt === 'expired' && r.decision === 'expired') || (filt === 'forced' && r.decision === 'approve_forced'));
  }, [d, filt]);
  const groups = useMemo(() => {
    const g = {};
    ((lab && lab.patterns) || []).forEach((p) => { (g[p.group] = g[p.group] || []).push(p); });
    return g;
  }, [lab]);

  if (!d) return <div className="card" style={{ margin: '1rem' }}>{err ? `⚠ ${err}` : 'Loading your decisions…'}</div>;
  const choice = (d.patterns || []).filter((p) => p.group === 'Your choice').sort((a, b) => b.n - a.n);
  const lessons = Object.entries(d.lessons || {}).sort((a, b) => String(b[1].first_seen).localeCompare(String(a[1].first_seen)));

  return (
    <div style={{ padding: '0.8rem', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: '0.8rem' }}>
        <div style={{ fontSize: '1.3rem', fontWeight: 800 }}>🧠 My Decisions</div>
        <div style={{ color: C.dim, fontSize: '0.8rem' }}>
          {d.with_result} results from {d.days} trading days · checked {String(d.generated).slice(11, 16)}
          {err && <span style={{ color: C.warn }}> · ⚠ {err} (showing the last data)</span>}
        </div>
      </div>

      <Section title="Your choices so far" sub="Approved = the real trade's result. Rejected / no answer = what the trade WOULD have made (last price, no spread - a bit better than a real fill).">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
          {choice.map((p) => <Tile key={p.label} p={p} />)}
        </div>
      </Section>

      {d.stock_bg && <StockBackground b={d.stock_bg} />}

      {d.wide && (
        <Section title="🎣 Wide net test - every stock signal the bot skipped (virtual, no money)"
                 sub={`1 lot each at the real option price + spread + Rs.110 charges, normal stock-option exits. ${d.wide.open || 0} open now. If this earns in both halves of the days, it can become real trading.`}>
          {(!d.wide.all || !d.wide.all.n) ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>Started today - the first results appear as the test trades close (within ~60 min each).</div> : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8, marginBottom: 8 }}>
                {[['All test trades', d.wide.all], ['Older half', d.wide.older], ['Newer half', d.wide.newer]].map(([t, a]) => (
                  <div key={t} className="card" style={{ margin: 0, padding: '0.6rem 0.8rem' }}>
                    <div style={{ color: C.txt2, fontSize: '0.78rem' }}>{t}</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: col(a && a.rs) }}>{a && a.n ? rs(a.rs) : '—'}</div>
                    <div style={{ fontSize: '0.78rem', color: C.txt2 }}>{a && a.n ? `${a.n} trades · ${a.win_pct}% won · ${rs(a.per_trade)} per trade` : 'no trades yet'}</div>
                  </div>))}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead><tr style={{ color: C.dim, textAlign: 'left' }}><th>Day</th><th>Trades</th><th>Won</th><th>Result</th><th>Per trade</th></tr></thead>
                  <tbody>{[...(d.wide.by_day || [])].reverse().map((r) => (
                    <tr key={r.day} style={{ borderTop: '1px solid var(--border, #232c3b)' }}>
                      <td style={{ padding: '3px 4px' }}>{r.day}</td><td>{r.n}</td><td>{r.win_pct}%</td>
                      <td style={{ color: col(r.rs) }}>{rs(r.rs)}</td><td style={{ color: col(r.per_trade) }}>{rs(r.per_trade)}</td>
                    </tr>))}</tbody>
                </table>
              </div>
              <div style={{ fontSize: '0.78rem', color: C.txt2, marginTop: 6 }}>
                By AI score: {Object.entries(d.wide.by_score || {}).map(([k, a]) => `${k}: ${a.n ? `${a.n} trades ${rs(a.rs)}` : '—'}`).join(' · ')}
              </div>
              <div style={{ marginTop: 6 }}>
                {(d.wide.recent || []).slice(0, 25).map((r, i) => (
                  <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: '0.78rem', borderTop: '1px solid var(--border, #232c3b)', padding: '3px 0' }}>
                    <span style={{ color: C.dim }}>{r.day.slice(5)} {r.time.slice(0, 5)}</span><b>{r.ts_}</b>
                    <span style={{ color: C.dim }}>score {r.score} · bot skipped: {r.gate}</span>
                    <span style={{ marginLeft: 'auto', color: col(r.rs), fontWeight: 700 }}>{pc(r.pct)} {rs(r.rs)} <span style={{ color: C.dim, fontWeight: 400 }}>({r.exit}, best {pc(r.best_pct)}, {r.mins} min)</span></span>
                  </div>))}
              </div>
            </>)}
        </Section>)}

      <div style={{ display: 'flex', gap: 6, margin: '0 0 0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ color: C.txt2, fontSize: '0.82rem' }}>Learn from:</span>
        <button className={`btn ${view === 'real' ? 'btn-start' : ''}`} onClick={() => setView('real')}>Real trades only ({d.real ? d.real.with_result : 0})</button>
        <button className={`btn ${view === 'all' ? 'btn-start' : ''}`} onClick={() => setView('all')}>All decisions incl. would-have ({d.with_result})</button>
      </div>

      <Section title="✅ When to take it - patterns that made money"
               sub="GOOD only when the older AND the newer half of the days were both in profit (the regular backtest).">
        {(lab.take || []).length === 0 ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>No pattern is reliably good yet - more results needed.</div> :
          lab.take.map((t) => (
            <div key={t.text} style={{ padding: '0.35rem 0', borderTop: '1px solid var(--border, #232c3b)' }}>
              <div style={{ fontWeight: 700, color: C.win, fontSize: '0.88rem' }}>{t.text}</div>
              <div style={{ fontSize: '0.78rem', color: C.txt2 }}>{t.odds}</div>
            </div>))}
      </Section>

      <Section title="⚠ Precautions - patterns that lost money" sub="BAD = lost in both halves of the days.">
        {(lab.caution || []).length === 0 ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>No pattern is reliably bad yet.</div> :
          lab.caution.map((t) => (
            <div key={t.text} style={{ padding: '0.35rem 0', borderTop: '1px solid var(--border, #232c3b)' }}>
              <div style={{ fontWeight: 700, color: C.loss, fontSize: '0.88rem' }}>{t.text}</div>
              <div style={{ fontSize: '0.78rem', color: C.txt2 }}>{t.odds}</div>
              <div style={{ fontSize: '0.78rem', color: C.warn }}>Precaution: {t.caution}</div>
            </div>))}
      </Section>

      <Section title="🔬 Every pattern - chance of profit and both halves"
               sub={`Older half = days before ${d.split_day || '—'}, newer half = from then. Same check runs every evening after 15:40.`} open={false}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead><tr style={{ color: C.dim, textAlign: 'left' }}>
              <th>Pattern</th><th>Picks</th><th>Chance of profit</th><th>Avg</th><th>Older</th><th>Newer</th><th>₹ (real)</th><th>Verdict</th>
            </tr></thead>
            <tbody>
              {Object.entries(groups).map(([g, list]) => (
                <React.Fragment key={g}>
                  <tr><td colSpan={8} style={{ paddingTop: 8, fontWeight: 800, color: C.txt2 }}>{g}</td></tr>
                  {list.map((p) => (
                    <tr key={g + p.label} style={{ borderTop: '1px solid var(--border, #232c3b)' }}>
                      <td style={{ padding: '3px 4px' }}>{p.label}</td><td>{p.n}</td>
                      <td style={{ color: col(p.win_pct - 50) }}>{p.win_pct}%</td>
                      <td style={{ color: col(p.avg_pct) }}>{pc(p.avg_pct)}</td>
                      <td style={{ color: col(p.older.avg_pct) }}>{pc(p.older.avg_pct)} <span style={{ color: C.dim }}>({p.older.n})</span></td>
                      <td style={{ color: col(p.newer.avg_pct) }}>{pc(p.newer.avg_pct)} <span style={{ color: C.dim }}>({p.newer.n})</span></td>
                      <td style={{ color: col(p.rs) }}>{rs(p.rs)}</td><td><Verdict v={p.verdict} /></td>
                    </tr>))}
                </React.Fragment>))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="🧠 What the AI remembers" sub="Saved every evening: when each lesson was first learned; a lesson that stops holding is marked faded (never deleted)." open={false}>
        {lessons.length === 0 ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>The first lessons are saved this evening after 15:40.</div> :
          lessons.map(([k, v]) => (
            <div key={k} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '0.3rem 0', borderTop: '1px solid var(--border, #232c3b)', fontSize: '0.82rem' }}>
              <Verdict v={v.verdict} /><span style={{ fontWeight: 600 }}>{k}</span>
              <span style={{ color: C.txt2 }}>{v.win_pct}% won, avg {pc(v.avg_pct)}, {v.n} picks</span>
              <span style={{ color: C.dim, marginLeft: 'auto' }}>learned {v.first_seen}{v.faded_on ? ` · faded ${v.faded_on}` : ` · still true ${v.last_seen}`}</span>
            </div>))}
      </Section>

      <Section title="📅 Regular backtest - every evening" sub="Each row = that evening's re-test of all your decisions." open={false}>
        {(d.history || []).length === 0 ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>First re-test runs this evening after 15:40.</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead><tr style={{ color: C.dim, textAlign: 'left' }}><th>Day</th><th>Results</th><th>Approved</th><th>Rejected (would have)</th><th>No answer (would have)</th><th>Good / bad patterns</th></tr></thead>
              <tbody>
                {[...d.history].reverse().map((h) => {
                  const b = h.by_choice || {};
                  const cell = (k) => b[k] ? <span style={{ color: col(b[k].avg_pct) }}>{b[k].win_pct}% · {pc(b[k].avg_pct)} ({b[k].n})</span> : '—';
                  return (
                    <tr key={h.day} style={{ borderTop: '1px solid var(--border, #232c3b)' }}>
                      <td style={{ padding: '3px 4px' }}>{h.day}</td><td>{h.with_result}</td><td>{cell('you approved')}</td>
                      <td>{cell('you rejected')}</td><td>{cell('no answer (card expired)')}</td>
                      <td><span style={{ color: C.win }}>{(h.good || []).length}</span> / <span style={{ color: C.loss }}>{(h.bad || []).length}</span></td>
                    </tr>);
                })}
              </tbody>
            </table>
          </div>)}
      </Section>

      <Section title="📋 Every decision - why it went up or down" sub="Tap a row for the full reasons, the skip reason and who traded it.">
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
          {[['all', 'All'], ['profit', '▲ Profit'], ['loss', '▼ Loss'], ['approved', 'Approved'], ['forced', 'Approved anyway'], ['rejected', 'Rejected'], ['expired', 'No answer']].map(([k, t]) => (
            <button key={k} className={`btn ${filt === k ? 'btn-start' : ''}`} style={{ padding: '0.25rem 0.7rem', fontSize: '0.8rem' }} onClick={() => setFilt(k)}>{t}</button>))}
        </div>
        {rows.length === 0 ? <div style={{ color: C.dim, fontSize: '0.85rem' }}>Nothing here.</div> : rows.slice(0, 200).map((r) => <DecisionRow key={r.id + r.decision} r={r} />)}
      </Section>
    </div>
  );
}
